import { createDevUuid } from './createDevUuid';
import DevAuthHelper from './DevAuthHelper';
import { oidcUserStorageKey } from './oidcStorage';

const DEV_MODE_STORAGE_KEY = 'dev-mode-enabled';
const DEV_OIDC_ACCESS_TOKEN_STORAGE_KEY = ['cw-dev', 'oidc', 'access-token'].join('-');

function truthyEnv(v: string | undefined): boolean {
  const s = String(v ?? '').trim().toLowerCase();
  return s === '1' || s === 'true' || s === 'yes' || s === 'on';
}

/**
 * Dev-only mock access token: real SSO token if stored, else a stable per-tab UUID.
 * Never a hardcoded credential literal.
 */
export function getStableDevAccessToken(): string {
  const storedSso = DevAuthHelper.getStoredSSOToken();
  if (storedSso) return storedSso;

  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const existing = sessionStorage.getItem(DEV_OIDC_ACCESS_TOKEN_STORAGE_KEY);
      if (existing) return existing;
      const generated = createDevUuid();
      sessionStorage.setItem(DEV_OIDC_ACCESS_TOKEN_STORAGE_KEY, generated);
      return generated;
    }
  } catch {
    // sessionStorage unavailable (private mode / SSR)
  }

  return createDevUuid();
}

/** Must match `main.tsx` AuthProvider `authority` + `client_id` for react-oidc-context storage. */
export function devOidcUserStorageKey(): string {
  return oidcUserStorageKey();
}

function buildFakeOidcUser() {
  const ssoToken = getStableDevAccessToken();
  return {
    access_token: ssoToken,
    token_type: 'Bearer',
    scope: 'openid profile email roles approles',
    profile: {
      sub: 'dev-user-1',
      name: 'Development User',
      email: 'dev@connectwise.com',
    },
    expires_at: Math.floor(Date.now() / 1000) + 86400,
  };
}

/** Writes the dev OIDC stub to **localStorage** (same store as `WebStorageStateStore` in `main.tsx`). */
export function persistDevOidcUser(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  localStorage.setItem(devOidcUserStorageKey(), JSON.stringify(buildFakeOidcUser()));
}

/**
 * When skip-auth / dev-mode is on, seed once so `AuthProvider` loads as authenticated without a manual click.
 */
export function seedDevOidcUserIfMissing(): void {
  if (import.meta.env.PROD) return;
  if (!isDevModeLocalOverrideEnabled()) return;
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (localStorage.getItem(devOidcUserStorageKey())) return;
  persistDevOidcUser();
}

/**
 * Local dev bypass: `VITE_SKIP_AUTH` in `.env.development`, or `localStorage['dev-mode-enabled'] === 'true'`.
 * Disabled in production builds so a leftover flag cannot skip ConnectWise SSO on deployed hosts.
 */
export function isDevModeLocalOverrideEnabled(): boolean {
  if (import.meta.env.PROD) {
    return false;
  }
  if (truthyEnv(import.meta.env.VITE_SKIP_AUTH)) {
    return true;
  }
  try {
    return localStorage.getItem(DEV_MODE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}
